// src/pages/MetStationPage.js
import { DashboardMetStationHtml } from "../../components/skins/dashboard-metstation.ts";
import { globalState } from "../../framework/globalState.ts";
import { subscribeRegister } from "../../services/mqttClient.ts";
import { FullscreenToggle } from "../../components/utils/fullscreen-toggle.ts";
import { RechartsChartManager } from "../../components/charts/RechartsChartManager.tsx";
import { chartStyles } from "../../components/charts/ChartStyles.ts";
import { groupByDictionaryKey, computeFromMap, applyThreshold } from '../../services/registerUtils.ts';

export default function MetStationPage() {
  console.log("[MetStationPage] Initializing...");

  const container = document.createElement("div");
  container.classList.add("parent-container");

  // === Load Skin ===
  try {
    container.innerHTML = new DashboardMetStationHtml().metstation().trim();
    console.log("[MetStationPage] Skin loaded");
  } catch (error) {
    console.error("[MetStationPage] Skin error:", error);
    container.innerHTML = "<p>Error loading dashboard skin</p>";
    return container;
  }

  const siteWithData = globalState.currentSiteWithData;
  const site = (siteWithData as any)?.site || siteWithData;
  const registers = ((siteWithData as any)?.devices || []).flatMap((d: any) =>
        (d.registers || []).map((r: any) => ({ ...r, deviceName: d.name, deviceId: d._id }))
      );

  const subscriptions = [];

  if (!site?._id) {
    console.warn("[MetStationPage] No site selected");
    container.innerHTML += "<p class='text-center text-danger'>No site selected.</p>";
    return container;
  }

  console.log(`[MetStationPage] Site: ${site._id} (${site.name || 'Unnamed'})`);


  // === MET CONFIG KEYS ===
  const metConfigKeys = [
    "met_irradiance_poa1",
    "met_temperature_poa1",
    "met_horizontal_irradiance",
    "met_module_temperature",
    "met_ambient_temperature",
    "met_humidity",
    "met_pressure",
    "met_wind_speed",
    "met_wind_direction",
    "met_rainfall",
    "met_ref1_voc",
    "met_ref1_vshunt",
    "met_ref2_voc",
    "met_ref2_vshunt"
  ];

  const metGroups = groupByDictionaryKey(registers);
  const metSuffixes = new Set();

  metConfigKeys.forEach(key => {
    metGroups[key]?.forEach(g => {
      const match = g.name.match(/_(\d+)$/);
      if (match) metSuffixes.add(match[1]);
    });
  });

  const metUnits = Array.from(metSuffixes)
    .sort((a, b) => parseInt(a) - parseInt(b))
    .map((suffix, i) => ({ id: `MET ${(i + 1).toString().padStart(2, "0")}`, suffix }));

  if (metUnits.length === 0) metUnits.push({ id: "MET 01", suffix: "" });

  console.log(`[MetStationPage] Detected MET Units:`, metUnits.map(u => u.id).join(", "));

  // === DYNAMIC TABLES ===
  const mainTable = container.querySelector(".signal-values table");
  const refTable = container.querySelector(".ref-table table");

  if (!mainTable || !refTable) {
    console.error("[MetStationPage] Tables not found");
  } else {
    const mainThead = mainTable.querySelector("thead tr");
    const mainTbody = mainTable.querySelector("tbody");
    const refThead = refTable.querySelector("thead tr");
    const refTbody = refTable.querySelector("tbody");

    // === Add MET columns ===
    metUnits.slice(1).forEach(unit => {
      const thMain = document.createElement("th");
      thMain.textContent = unit.id;
      mainThead.appendChild(thMain);

      const thRef = document.createElement("th");
      thRef.textContent = unit.id;
      refThead.appendChild(thRef);

      mainTbody.querySelectorAll("tr").forEach(row => {
        const td = document.createElement("td");
        td.className = "text-right w-100 bg-subcontainer";
        td.textContent = "0.00";
        row.appendChild(td);
      });

      refTbody.querySelectorAll("tr").forEach(row => {
        const td = document.createElement("td");
        td.className = "text-right w-100 bg-subcontainer";
        td.textContent = "0.00";
        row.appendChild(td);
      });
    });

    // === Label mapping ===
    const labelMap = {
      "Irradiance POA 1": "met_irradiance_poa1",
      "Temp. POA 1": "met_temperature_poa1",
      "Horizontal Irradiance": "met_horizontal_irradiance",
      "WP Module Temp.": "met_module_temperature",
      "Ambient Temp.": "met_ambient_temperature",
      "Relative Humidity": "met_humidity",
      "Barometric Pressure": "met_pressure",
      "Wind Speed": "met_wind_speed",
      "Wind Direction": "met_wind_direction",
      "Rainfall Sensor": "met_rainfall",
      "Ref. 1 VOC": "met_ref1_voc",
      "Ref. 1 VSHUNT": "met_ref1_vshunt",
      "Ref. 2 VOC": "met_ref2_voc",
      "Ref. 2 VSHUNT": "met_ref2_vshunt"
    };

    // Set labels
    mainTbody.querySelectorAll("tr").forEach(row => {
      const label = row.children[0]?.textContent?.trim();
      if (!label) return;
      const configKey = labelMap[label];
      if (configKey) row.setAttribute("data-config", configKey);
    });

    refTbody.querySelectorAll("tr").forEach(row => {
      const label = row.children[0]?.textContent?.trim();
      if (!label) return;
      const configKey = labelMap[label];
      if (configKey) row.setAttribute("data-config", configKey);
    });
  }

  // === SUMMARY ELEMENTS ===
  const summaryElements = {
    irradiance: container.querySelector('[data-register="Irradiance POA 1"]'),
    temperature: container.querySelector('[data-register="Temp. POA 1"]'),
    windSpeed: container.querySelector('[data-register="Wind Speed"]'),
    pressure: container.querySelector('[data-register="Barometric Pressure"]'),
    humidity: container.querySelector('[data-register="Relative Humidity"]'),
  };

  const summaryMapping = {
    irradiance: 'met_irradiance_poa1',
    temperature: 'met_temperature_poa1',
    windSpeed: 'met_wind_speed',
    pressure: 'met_pressure',
    humidity: 'met_humidity',
  };

  const summaryValues = {};

  // ========================================
  // INSTANT INITIAL VALUES – REAL DATA ON FIRST LOAD!
  // ========================================
  metConfigKeys.forEach(configKey => {
    if (!summaryValues[configKey]) summaryValues[configKey] = {};
    const group = metGroups[configKey] || [];
    group.forEach(reg => {
      if (reg.value == null) return;
      const value = reg.value as number;
      summaryValues[configKey][reg.name] = value;
      const match = reg.name.match(/_(\d+)$/);
      const idx = match ? match[1] : "";
      const metIndex = metUnits.findIndex(u => u.suffix === idx);
      if (metIndex < 0) return;
      const isRef = ["met_ref1_voc", "met_ref1_vshunt", "met_ref2_voc", "met_ref2_vshunt"].includes(configKey);
      const table = isRef ? refTable : mainTable;
      const row = table?.querySelector(`tr[data-config="${configKey}"]`);
      if (row) {
        const targetCell = row.children[2 + metIndex] as HTMLElement;
        if (targetCell) {
          targetCell.textContent = value.toFixed(2);
          applyThreshold(targetCell, value, reg.meta);
        }
      }
    });
  });

  // Force immediate summary update with real values
  requestAnimationFrame(() => {
    updateSummary();
  });

  function updateSummary() {
    Object.entries(summaryMapping).forEach(([key, configKey]) => {
      const el = summaryElements[key] as HTMLElement;
      if (!el) return;
      const group = metGroups[configKey] || [];
      const value = computeFromMap(group, summaryValues[configKey] || {});
      if (value !== null) {
        el.textContent = value.toFixed(2);
        applyThreshold(el, value, group[0]?.meta);
      } else {
        el.textContent = "N/A";
        el.style.backgroundColor = "";
      }
    });
  }

  // === SUBSCRIBE TO ALL MET REGISTERS ===
  console.log(`[MetStationPage] Subscribing to ${metConfigKeys.length} metric groups`);

  metConfigKeys.forEach(configKey => {
    const group = metGroups[configKey] || [];
    if (!summaryValues[configKey]) summaryValues[configKey] = {};
    group.forEach(reg => {
      const unsub = subscribeRegister(site._id, reg.deviceId, reg._id, payload => {
        const raw = payload?.value ?? null;
        const value = typeof raw === 'number' ? raw : null;
        summaryValues[configKey][reg.name] = value;
        const match = reg.name.match(/_(\d+)$/);
        const idx = match ? match[1] : "";
        const metIndex = metUnits.findIndex(u => u.suffix === idx);
        if (metIndex < 0) return;
        const isRef = ["met_ref1_voc", "met_ref1_vshunt", "met_ref2_voc", "met_ref2_vshunt"].includes(configKey);
        const table = isRef ? refTable : mainTable;
        const row = table?.querySelector(`tr[data-config="${configKey}"]`);
        if (row && value !== null) {
          const targetCell = row.children[2 + metIndex] as HTMLElement;
          if (targetCell) {
            targetCell.textContent = value.toFixed(2);
            applyThreshold(targetCell, value, reg.meta);
          }
        }
        updateSummary();
      });
      subscriptions.push(unsub);
    });
  });

  // === CHARTS ===
  console.log("[MetStationPage] Initializing charts");
  const chartManager = new RechartsChartManager(site, registers);

  // Irradiance POA
  chartManager.createChart("refCellIrradianceChart", [], { ...chartStyles.IrradiancePOA, showControls: true }, container);
  chartManager.bindRegisterToChart("refCellIrradianceChart", [
    { configKey: "met_irradiance_poa1", mode: "average", name: "Irradiance POA 1" }
  ]);

  // Horizontal Irradiance
  chartManager.createChart("horizontalIrradianceChart", [], { ...chartStyles.horizontalIrradianceLayout, showControls: true }, container);
  chartManager.bindRegisterToChart("horizontalIrradianceChart", [
    { configKey: "met_horizontal_irradiance", mode: "average", name: "Horizontal Irradiance" }
  ]);

  // Module Temperature
  chartManager.createChart("moduleTemperatureChart", [], { ...chartStyles.moduleTemperatureLayout, showControls: true }, container);
  chartManager.bindRegisterToChart("moduleTemperatureChart", [
    { configKey: "met_module_temperature", mode: "average", name: "Module Temperature" }
  ]);

  // Wind Speed
  chartManager.createChart("windSpeedChart", [], { ...chartStyles.windSpeedLayout, showControls: true }, container);
  chartManager.bindRegisterToChart("windSpeedChart", [
    { configKey: "met_wind_speed", mode: "average", name: "Wind Speed" }
  ]);

  // Pressure
  chartManager.createChart("pressureChart", [], { ...chartStyles.pressureLayout, showControls: true }, container);
  chartManager.bindRegisterToChart("pressureChart", [
    { configKey: "met_pressure", mode: "average", name: "Barometric Pressure" }
  ]);

  // === INIT & CLEANUP ===
  requestAnimationFrame(() => {
    console.log("[MetStationPage] Initializing fullscreen toggle");
    new FullscreenToggle().init();
  });

  container.cleanup = () => {
    console.log(`[MetStationPage] Cleaning up ${subscriptions.length} subscriptions`);
    subscriptions.forEach(unsub => typeof unsub === "function" && unsub());
    chartManager.cleanup();
  };

  console.log("[MetStationPage] Initialization complete");
  return container;
}